import { useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../api';
import { updateUser } from './useUpdateUser';

type User = {
  phone?: string;
  email?: string;
  username?: string;
  password?: string;
  options?: any;
};

type UserWithEmail = {
  email: string;
  password: string;
  options?: any;
};

const createUserWithPhone = async (phone: string) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz1234567890';
  const password = new Array(8)
    .fill('')
    .map(() => chars[Math.floor(chars.length * Math.random())])
    .join('');
  const { data, error: signUpError } = await supabase.auth.signUp({
    password,
    phone,
  });

  if (signUpError && signUpError.message !== 'User already registered') {
    throw signUpError;
  }

  if (signUpError || !data.user?.identities?.length) {
    // user exist and phone is already confirmed

    const { data: loginData, error: loginError } = await supabase.auth.signInWithOtp({
      phone,
    });

    if (loginError) {
      throw new Error(loginError.message);
    }

    // @ts-ignore
    if (window.analytics) {
      // @ts-ignore
      window.analytics.identify(data.user?.id, {
        name: data.user?.user_metadata.full_name,
        email: data.user?.email,
      });
    }

    return loginData;
  }

  // @ts-ignore
  updateUser({ phone: phone.slice(1), id: data?.user?.id })

  return data;
};

const createUserWithEmail = async ({
  email = '',
  password = '',
  options,
}: UserWithEmail) => {
  const { data: userWithEmail } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('email', email)
    .single();

  if (userWithEmail?.is_deleted) {
    const { data: updatedData } = await supabase
      .from('user_profiles')
      .update({
        is_deleted: true,
      })
      .eq('id', userWithEmail?.id)
      .select('*')
      .single()

    await supabase.auth.signInWithPassword(
      {
        email,
        password,
      },
    )

    return updatedData
  }

  if (userWithEmail) {
    throw new Error('Seems like you have an account');
  }

  const { data, error: signUpError } = await supabase.auth.signUp({
    email,
    password,
    options: {
      ...options,
      data: {
        ...options?.data,
        isPassSet: true,
      },
    },
  });

  // @ts-ignore
  updateUser({ email, id: data?.user?.id })

  if (signUpError) {
    throw signUpError;
  }

  // @ts-ignore
  if (window.analytics) {
    // @ts-ignore
    window.analytics.identify(data.user?.id, {
      name: data.user?.user_metadata.full_name,
      email: data.user?.email,
    });
  }

  return data;
};

export default function useCreateUser({
  phone = '',
  email = '',
  password = '',
  options,
}: User) {
  const queryClient = useQueryClient();

  return useMutation(
    () => {
      if (phone) return createUserWithPhone(phone);
      return createUserWithEmail({ email, password, options });
    },
    {
      onSuccess: () => {
        queryClient.removeQueries({ queryKey: ['isAuth'] });
        queryClient.removeQueries({ queryKey: ['user'] });
        queryClient.removeQueries({ queryKey: ['profile'] });
      },
    },
  );
}
